/**
 * RequestTasksSection.tsx
 * 
 * Componente per la gestione dei task associati a una richiesta.
 * Mostra i task esistenti e permette di crearne di nuovi.
 */

import React, { useState } from 'react';
import {
  Stack,
  Title,
  Group,
  Button,
  Card,
  Text,
  Badge,
  ActionIcon,
  Table,
  Progress,
  Alert,
  Modal,
  Grid,
  Box
} from '@mantine/core';
import { 
  IconPlus, 
  IconEdit, 
  IconTrash, 
  IconLayoutKanban, 
  IconCheck, 
  IconClock, 
  IconPlayerPlay,
  IconCircleDot,
  IconTool
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useRequestTasks, useRequestTasksStats } from '../../../hooks/use-request-tasks';
import { useQuery } from '@tanstack/react-query';
import { usersNew } from '../../../services/api';
import { ProjectTask, TaskStatus, User } from '../../../types/api';
import { getTaskStatusColor, getTaskStatusLabel } from '../../../utils/task-status-utils';
import TaskCreateModal from '../../../components/modals/TaskCreateModal';
import TaskDetailModal from '../../../components/modals/TaskDetailModal';

interface RequestTasksSectionProps {
  requestId: string;
  disabled?: boolean;
  onTaskUpdate?: () => void;
}

// Le etichette/colori dello stato ora vengono da task-status-utils (Iniziato/Completato);
// questa mappa resta solo per la scelta dell'icona decorativa in base allo stato puntuale.
const TASK_STATUS_ICONS = {
  'Ideas': IconCircleDot,
  'ToDo': IconClock,
  'InProgress': IconPlayerPlay,
  'Doing': IconPlayerPlay,
  'Testing': IconTool,
  'Done': IconCheck,
  'Cancelled': IconTrash,
} as const;

export function RequestTasksSection({ requestId, disabled = false, onTaskUpdate }: RequestTasksSectionProps) {
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);

  const { 
    tasks, 
    isLoading, 
    createTaskMutation, 
    updateTaskStatusMutation, 
    deleteTaskMutation 
  } = useRequestTasks(requestId);
  
  const { data: stats } = useRequestTasksStats(requestId);
  // Hook per ottenere tutti gli utenti attivi (non paginato)
  const { data: activeUsers } = useQuery({
    queryKey: ['users', 'active'],
    queryFn: () => usersNew.getActiveUsers(),
    staleTime: 5 * 60 * 1000, // 5 minuti
  });

  // Helper function per trovare l'utente assegnato (fallback legacy, singolo assegnatario)
  const getAssignedUserInfo = (task: ProjectTask) => {
    if (task.assignedToUser) {
      return {
        name: task.assignedToUser.fullName,
        email: task.assignedToUser.email
      };
    }

    if (task.assignedToUserId && activeUsers) {
      const user = activeUsers.find((u: User) => u.id == task.assignedToUserId?.toString());
      if (user) {
        return {
          name: user.fullName || (user.firstName + ' ' + user.lastName),
          email: user.email
        };
      }
    }

    return null;
  };

  // Assegnatari multipli (task.assignees, se il backend li restituisce), con fallback
  // sul lookup legacy a singolo assegnatario quando il campo non è presente.
  const getAssigneesDisplay = (task: ProjectTask): string => {
    if (task.assignees) {
      return task.assignees.length > 0 ? task.assignees.map((a) => a.fullName).join(', ') : 'Non assegnato';
    }
    const userInfo = getAssignedUserInfo(task);
    return userInfo ? userInfo.name : 'Non assegnato';
  };

  const handleCreateTask = () => {
    setShowNewTaskModal(true);
  };

  const handleEditTask = (task: ProjectTask) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (confirm('Sei sicuro di voler eliminare questo task?')) {
      await deleteTaskMutation.mutateAsync(taskId);
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: TaskStatus) => {
    await updateTaskStatusMutation.mutateAsync({ taskId, status: newStatus });
  };

  const handleGoToKanban = () => {
    navigate({ to: '/board', search: { requestId } });
  };

  const handleTaskCreated = (taskId: number) => {
    // Il TaskCreateModal gestisce già la notificazione, noi gestiamo solo il refresh
    setShowNewTaskModal(false);
    // Forza il refresh dei task tramite l'hook useRequestTasks
    onTaskUpdate?.();
  };

  const handleTaskCancel = () => {
    setShowTaskModal(false);
    setShowNewTaskModal(false);
    setSelectedTask(null);
  };

  if (isLoading) {
    return (
      <Card>
        <Text>Caricamento task...</Text>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Task Collegati</Title>
        <Group>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateTask}
            disabled={disabled}
            size="sm"
          >
            Nuovo Task
          </Button>
          <Button
            leftSection={<IconLayoutKanban size={16} />}
            onClick={handleGoToKanban}
            variant="light"
            size="sm"
          >
            Vai alla Board
          </Button>
        </Group>
      </Group>

      {/* Statistiche */}
      {stats && (
        <Grid>
          <Grid.Col span={3}>
            <Card>
              <Text size="sm" c="dimmed">Totale Task</Text>
              <Text size="lg" fw={700}>{stats.total}</Text>
            </Card>
          </Grid.Col>
          <Grid.Col span={3}>
            <Card>
              <Text size="sm" c="dimmed">Da Fare</Text>
              <Text size="lg" fw={700} c="blue">{stats.todo}</Text>
            </Card>
          </Grid.Col>
          <Grid.Col span={3}>
            <Card>
              <Text size="sm" c="dimmed">In Corso</Text>
              <Text size="lg" fw={700} c="yellow">{stats.inProgress}</Text>
            </Card>
          </Grid.Col>
          <Grid.Col span={3}>
            <Card>
              <Text size="sm" c="dimmed">Completati</Text>
              <Text size="lg" fw={700} c="green">{stats.done}</Text>
            </Card>
          </Grid.Col>
        </Grid>
      )}

      {/* Progress bar */}
      {stats && stats.total > 0 && (
        <Box>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>Progresso</Text>
            <Text size="sm" c="dimmed">{stats.completionRate.toFixed(1)}%</Text>
          </Group>
          <Progress value={stats.completionRate} size="lg" />
        </Box>
      )}

      {/* Lista Task */}
      {tasks && tasks.length > 0 ? (
        <Card>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Task</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th>Assegnato a</Table.Th>
                <Table.Th>Scadenza</Table.Th>
                <Table.Th style={{ width: 120 }}>Azioni</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tasks.map((task) => {
                // Usa statusString dal backend invece dell'enum
                const statusKey = task.statusString || task.status || 'ToDo';
                const StatusIcon = TASK_STATUS_ICONS[statusKey as keyof typeof TASK_STATUS_ICONS] || IconClock;
                
                return (
                  <Table.Tr key={task.id}>
                    <Table.Td>
                      <Box>
                        <Text fw={500}>{task.name || task.title}</Text>
                        {task.description && (
                          <Text size="sm" c="dimmed" truncate>
                            {task.description}
                          </Text>
                        )}
                      </Box>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={getTaskStatusColor(task)}
                        variant="light"
                        leftSection={<StatusIcon size={12} />}
                      >
                        {getTaskStatusLabel(task)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {(() => {
                        const display = getAssigneesDisplay(task);
                        if (display === 'Non assegnato') {
                          return <Text size="sm" c="dimmed">Non assegnato</Text>;
                        }
                        return <Text size="sm" fw={500}>{display}</Text>;
                      })()}
                    </Table.Td>
                    <Table.Td>
                      {task.dueDate ? (
                        <Text size="sm">
                          {new Date(task.dueDate).toLocaleDateString()}
                        </Text>
                      ) : task.endDate ? (
                        <Text size="sm">
                          {new Date(task.endDate).toLocaleDateString()}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => handleEditTask(task)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDeleteTask(task.id)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Card>
      ) : (
        <Alert
          icon={<IconLayoutKanban size={16} />}
          title="Nessun task"
          color="gray"
        >
          Nessun task associato a questa richiesta.
          <Button
            mt="sm"
            variant="light"
            size="sm"
            onClick={handleCreateTask}
            disabled={disabled}
          >
            Crea il primo task
          </Button>
        </Alert>
      )}

      {/* Modal per Task Detail */}
      {selectedTask && (
        <TaskDetailModal
          opened={showTaskModal}
          onClose={handleTaskCancel}
          taskId={parseInt(selectedTask.id)}
        />
      )}

      {/* Modal per Nuovo Task */}
      <TaskCreateModal
        opened={showNewTaskModal}
        onClose={handleTaskCancel}
        onTaskCreated={handleTaskCreated}
        requestId={requestId}
      />
    </Stack>
  );
}
